/**
 * Linked Organizations Tab
 * Shows non-hierarchical links between organizations with add/edit/remove
 */

import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import {
  getLinkedOrganizations,
  searchOrganizations,
  linkOrganization,
  unlinkOrganization,
  updateOrganizationLink,
} from "../../services/organizations.service";
import { ORGANIZATION_TYPE_OPTIONS } from "../../types/organization.types";
import type { Organization } from "../../types/organization.types";
import { useToast } from "../ui/Toast";
import Card from "../ui/Card";
import Button from "../common/Button";
import LoadingSpinner from "../common/LoadingSpinner";
import {
  LinkIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  CheckIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";

interface LinkedOrganizationsTabProps {
  organizationId: string;
}

export default function LinkedOrganizationsTab({
  organizationId,
}: LinkedOrganizationsTabProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  // Add link modal state
  const [showAddForm, setShowAddForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<Organization[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [linkDescription, setLinkDescription] = useState("");
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Edit description state
  const [editingLinkId, setEditingLinkId] = useState<string | null>(null);
  const [editDescription, setEditDescription] = useState("");

  // Confirm delete state
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  // Fetch linked organizations
  const { data: links = [], isLoading } = useQuery({
    queryKey: ["organization-links", organizationId],
    queryFn: () => getLinkedOrganizations(organizationId),
    enabled: !!organizationId,
  });

  // Debounced search
  useEffect(() => {
    if (!searchQuery.trim() || selectedOrg) {
      setSearchResults([]);
      return;
    }

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        const results = await searchOrganizations(searchQuery);
        // Exclude current org and already-linked orgs
        const linkedIds = new Set(links.map((l) => l.linked_organization.id));
        linkedIds.add(organizationId);
        setSearchResults(results.filter((r) => !linkedIds.has(r.id)));
      } catch {
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery, organizationId, links, selectedOrg]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setSearchResults([]);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Link mutation
  const linkMutation = useMutation({
    mutationFn: async () => {
      if (!selectedOrg) {
        throw new Error("No organization selected");
      }
      await linkOrganization(
        organizationId,
        selectedOrg.id,
        linkDescription || undefined,
        user?.id,
      );
    },
    onSuccess: () => {
      showToast("success", "Organization linked successfully");
      queryClient.invalidateQueries({
        queryKey: ["organization-links", organizationId],
      });
      resetAddForm();
    },
    onError: (error: Error) => {
      showToast("error", error.message);
    },
  });

  // Unlink mutation
  const unlinkMutation = useMutation({
    mutationFn: (linkId: string) => unlinkOrganization(linkId),
    onSuccess: () => {
      showToast("success", "Organization unlinked");
      queryClient.invalidateQueries({
        queryKey: ["organization-links", organizationId],
      });
      setConfirmDeleteId(null);
    },
    onError: (error: Error) => {
      showToast("error", `Failed to unlink: ${error.message}`);
    },
  });

  // Update description mutation
  const updateMutation = useMutation({
    mutationFn: ({
      linkId,
      description,
    }: {
      linkId: string;
      description: string;
    }) => updateOrganizationLink(linkId, description),
    onSuccess: () => {
      showToast("success", "Description updated");
      queryClient.invalidateQueries({
        queryKey: ["organization-links", organizationId],
      });
      setEditingLinkId(null);
    },
    onError: (error: Error) => {
      showToast("error", `Failed to update: ${error.message}`);
    },
  });

  const resetAddForm = () => {
    setShowAddForm(false);
    setSearchQuery("");
    setSearchResults([]);
    setSelectedOrg(null);
    setLinkDescription("");
  };

  const handleSelectOrg = (org: Organization) => {
    setSelectedOrg(org);
    setSearchQuery(org.name);
    setSearchResults([]);
  };

  const getOrgTypeLabel = (typeValue?: string) => {
    if (!typeValue) {
      return "";
    }
    return (
      ORGANIZATION_TYPE_OPTIONS.find((o) => o.value === typeValue)?.label ||
      typeValue
    );
  };

  if (isLoading) {
    return (
      <Card>
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
        </div>
      </Card>
    );
  }

  return (
    <Card>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900">
          Linked Organizations
        </h2>
        {!showAddForm && (
          <Button onClick={() => setShowAddForm(true)}>
            <LinkIcon className="h-5 w-5 mr-1" />
            Link Organization
          </Button>
        )}
      </div>

      {/* Add Link Form */}
      {showAddForm && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-700">
              Link an Organization
            </h3>
            <button
              onClick={resetAddForm}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Search / Select Org */}
          <div className="relative mb-3" ref={dropdownRef}>
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  if (selectedOrg) {
                    setSelectedOrg(null);
                  }
                }}
                placeholder="Search organizations by name..."
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
              {isSearching && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <LoadingSpinner size="sm" />
                </div>
              )}
            </div>

            {/* Search Results Dropdown */}
            {searchResults.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-auto">
                {searchResults.map((org) => (
                  <button
                    key={org.id}
                    type="button"
                    onClick={() => handleSelectOrg(org)}
                    className="w-full text-left px-4 py-2 hover:bg-blue-50 text-sm border-b border-gray-100 last:border-0"
                  >
                    <span className="font-medium text-gray-900">
                      {org.name}
                    </span>
                    {org.organization_type && (
                      <span className="ml-2 text-xs text-gray-500">
                        {getOrgTypeLabel(org.organization_type)}
                      </span>
                    )}
                    {(org.city_tag || org.state_tag) && (
                      <span className="ml-2 text-xs text-gray-400">
                        {[org.city_tag, org.state_tag]
                          .filter(Boolean)
                          .join(", ")}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Description */}
          <div className="mb-3">
            <input
              type="text"
              value={linkDescription}
              onChange={(e) => setLinkDescription(e.target.value)}
              placeholder="Relationship description (e.g., Marketing partnership)"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={resetAddForm}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={() => linkMutation.mutate()}
              disabled={!selectedOrg}
              isLoading={linkMutation.isPending}
            >
              Link
            </Button>
          </div>
        </div>
      )}

      {/* Links Table */}
      {links.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Organization
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {links.map((link) => (
                <tr key={link.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() =>
                        navigate(
                          `/organizations/${link.linked_organization.id}`,
                        )
                      }
                      className="text-sm font-medium text-blue-600 hover:text-blue-800"
                    >
                      {link.linked_organization.name}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {getOrgTypeLabel(
                      link.linked_organization.organization_type,
                    ) || "—"}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {editingLinkId === link.id ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={editDescription}
                          onChange={(e) => setEditDescription(e.target.value)}
                          className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              updateMutation.mutate({
                                linkId: link.id,
                                description: editDescription,
                              });
                            }
                            if (e.key === "Escape") {
                              setEditingLinkId(null);
                            }
                          }}
                        />
                        <button
                          onClick={() =>
                            updateMutation.mutate({
                              linkId: link.id,
                              description: editDescription,
                            })
                          }
                          className="text-green-600 hover:text-green-800"
                        >
                          <CheckIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setEditingLinkId(null)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <span
                        className={
                          link.description
                            ? "text-gray-700"
                            : "text-gray-400 italic"
                        }
                      >
                        {link.description || "No description"}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-3">
                    {editingLinkId !== link.id && (
                      <button
                        onClick={() => {
                          setEditingLinkId(link.id);
                          setEditDescription(link.description || "");
                        }}
                        className="text-blue-600 hover:text-blue-900 transition-colors"
                        title="Edit description"
                      >
                        <PencilIcon className="h-4 w-4 inline" />
                      </button>
                    )}
                    {confirmDeleteId === link.id ? (
                      <span className="inline-flex items-center gap-2">
                        <span className="text-xs text-gray-500">Unlink?</span>
                        <button
                          onClick={() => unlinkMutation.mutate(link.id)}
                          className="text-red-600 hover:text-red-800 font-medium"
                        >
                          Yes
                        </button>
                        <button
                          onClick={() => setConfirmDeleteId(null)}
                          className="text-gray-500 hover:text-gray-700"
                        >
                          No
                        </button>
                      </span>
                    ) : (
                      <button
                        onClick={() => setConfirmDeleteId(link.id)}
                        className="text-red-600 hover:text-red-900 transition-colors"
                        title="Unlink"
                      >
                        <TrashIcon className="h-4 w-4 inline" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-12">
          <LinkIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            No linked organizations
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Use the Link Organization button to associate related organizations.
          </p>
          {!showAddForm && (
            <div className="mt-6">
              <Button onClick={() => setShowAddForm(true)}>
                <LinkIcon className="h-5 w-5 mr-1" />
                Link First Organization
              </Button>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
